import { COLOR_DARK, COLOR_LIGHT, TRACKING_KEY, TRACKING_PROPERTIES_KEY } from './global/constants';

// Utils
// import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import isNumber from 'lodash/isNumber';
import Color from 'color';
import emojiRegex from 'emoji-regex';
import formatDateIntl from 'date-fns/intlFormat/index';
import debug from 'debug';

// Types
import type { TrackingProperty } from '$lib/types/TrackingProperty';
import type { Component, ExtendedRawDraftInlineStyleRange } from './types/Components/SharedTypes';
import type { RawDraftContentState } from 'draft-js';
import type { Language } from '$lib/types/Language';

declare global {
    interface String {
        splice(start: number, length: number, replacement: string): string;
    }
}

const debugLog = debug('lib:helper');

// Matches emoji with two spaces in the end
const emojiReg = emojiRegex();
const spacesReg = new RegExp(/  +/g);
const emojiWithSpacesRegex = new RegExp('(' + emojiReg.source + ')(' + spacesReg.source + ')');

// For personalization string replacement
String.prototype.splice = function (start: number, length: number, replacement: string) {
    return this.substr(0, start) + replacement + this.substr(start + length);
};

export const hexToRgb = (hex: string) => {
    // hex can be #123 or #112233
    const sanitizedHexString =
        hex.length === 4 ? '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3] : hex;

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(sanitizedHexString);

    if (!result) return { r: 0, g: 0, b: 0 };

    return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    };
};

export const rgbaToRgb = (rgba: string) => {
    const colors = rgba.match(/\d+/g);

    if (!colors) return { r: 0, g: 0, b: 0 };

    return {
        r: parseInt(colors[0], 16),
        g: parseInt(colors[1], 16),
        b: parseInt(colors[2], 16),
    };
};

export const isHexString = (color: string) => {
    return /^#([A-Fa-f0-9]{3}){1,2}$/i.test(color);
};

export const isRgbaString = (color: string) => {
    return /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/.test(color);
};

export const isDark = (color: string) => {
    if (color === 'transparent') {
        return false;
    }

    try {
        const [r, g, b] = Color(color).rgb().array();
        const yiq = (r * 2126 + g * 7152 + b * 722) / 10000;
        return yiq < 128;
    } catch {
        return false;
    }
};

export const getColorByLightness = (refColor = '#000000') => {
    if (refColor.includes('linear-gradient')) return COLOR_LIGHT;

    return Color(refColor).lightness() <= 60 ? COLOR_LIGHT : COLOR_DARK;
};

/**
 * Get cookie preferences from local storage
 */
export const getCookiePreferences = (campaignId: string) => {
    const LS = window.localStorage;
    const preferences = LS.getItem(`${TRACKING_KEY}${campaignId}`);

    if (preferences) {
        return JSON.parse(preferences);
    }

    return {};
};

/**
 * Check if preferences are present in LS or not
 */
export const getCookiePreferencesPresent = (campaignId: string) => {
    const LS = window.localStorage;
    return !!LS.getItem(`${TRACKING_KEY}${campaignId}`);
};

/**
 * Set cookie preferences in local storage
 */
export const setCookiePreferences = (campaignId: string, preferences: { [key: string]: any }) => {
    const LS = window.localStorage;

    return LS.setItem(`${TRACKING_KEY}${campaignId}`, JSON.stringify(preferences));
};

/**
 * Add Http to URLs without http://, https://, tel: or mailto: prefix
 * @param url
 * @returns {string|*}
 */
export const createExternalUrl = (url: string) => {
    if (!/(^mailto:)|(^tel:)|(^https?:\/\/)/i.test(url)) {
        return 'http://' + url;
    }

    return url;
};

export const setTrackingProperty = (
    key: string,
    value: string | undefined,
    campaignId: string | undefined,
) => {
    const ST = window.sessionStorage;
    const personalization = JSON.parse(
        ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`) || '{}',
    );

    const updatedPersonalization = personalization ? { ...personalization } : {};

    updatedPersonalization[key] = value;

    ST.setItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`, JSON.stringify(updatedPersonalization));
};

export const getAllTrackingProperties = (campaignId: string) => {
    const ST = window.sessionStorage;
    const properties = ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`);

    if (!properties) return {};

    return JSON.parse(ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`) || '{}');
};

export const getDefaultTrackingProperties = (trackingProperties: TrackingProperty[]) => {
    const mappedTrackingProperties: { [fieldName: string]: string } = {};

    trackingProperties.forEach((property) => {
        mappedTrackingProperties[property.fieldName] = property.defaultValue;
    });

    return mappedTrackingProperties;
};

const getMentionInlineStyles = (draftState: RawDraftContentState) => {
    const styles: { [entityKey: number]: ExtendedRawDraftInlineStyleRange[] } = {};

    draftState.blocks.forEach((block) => {
        block.entityRanges.forEach((entity) => {
            const affectedStyles: ExtendedRawDraftInlineStyleRange[] = [];

            block.inlineStyleRanges.forEach((style: ExtendedRawDraftInlineStyleRange) => {
                const styleEnd = style.offset + style.length;
                const entityEnd = entity.offset + entity.length;

                // If mention comes before or within style range
                if (
                    entityEnd < style.offset ||
                    (entityEnd >= style.offset && entityEnd <= styleEnd)
                ) {
                    style.originalEntityEnd = entityEnd;
                    style.originalEntityLength = entity.length;

                    affectedStyles.push(style);
                }
            });

            if (affectedStyles.length) {
                styles[entity.key] = affectedStyles;
            }
        });
    });

    return styles;
};

export const personalizeText = (
    draftState: RawDraftContentState,
    trackingProperties: TrackingProperty[],
    campaignId: string,
    language: Language = 'de',
) => {
    const defaultTrackingProperties = getDefaultTrackingProperties(trackingProperties);
    const savedTrackingProperties = getAllTrackingProperties(campaignId);

    // console.log('before:', cloneDeep(draftState));

    const inlineStylesMap = getMentionInlineStyles(draftState);

    // For each Block...
    draftState.blocks.forEach((block) => {
        let offsetChange = 0;

        // iterate over entities...
        block.entityRanges.forEach((entity) => {
            // Adjust entity offset by previous entity
            entity.offset += offsetChange;

            // iterate of tracking properties
            Object.keys(defaultTrackingProperties).forEach((fieldName) => {
                // if entity is a mention...
                if (draftState.entityMap[entity.key].type === 'mention') {
                    // ... and entity is pointing to tracking property
                    if (fieldName === draftState.entityMap[entity.key].data.mention.fieldName) {
                        // get replace value (from session storage or default)
                        let replaceValue = savedTrackingProperties[fieldName]
                            ? savedTrackingProperties[fieldName]
                            : defaultTrackingProperties[fieldName];

                        // Check if replaceValue is an ISO date string and make it more readable
                        replaceValue = parseISODate(replaceValue, language);

                        // remove double spaces (after emojis)
                        const needsReplacement = emojiWithSpacesRegex.test(block.text);

                        if (needsReplacement) {
                            block.text = block.text.replace(emojiWithSpacesRegex, '$1 ');
                        }

                        if (replaceValue) {
                            // replace text in current block
                            block.text = block.text.splice(
                                entity.offset,
                                entity.length,
                                replaceValue,
                            );

                            // calculate offset change
                            const newLength = replaceValue.toString().length;
                            offsetChange += newLength - entity.length;

                            // update entity length
                            entity.length = newLength;
                        }
                    }
                }
            });
        });
    });

    const updatedDraftState = applyInlineStyles(draftState, inlineStylesMap);

    // console.log('after:', cloneDeep(updatedDraftState));
    // console.log('--------');

    return updatedDraftState;
};

const parseISODate = (str: string, locale: string) => {
    if (isIsoDate(str)) {
        const date = new Date(str);

        if (!isNaN(date.valueOf())) {
            return formatDateIntl(
                date,
                { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
                { locale },
            );
        }
    }

    return str;
};

const isIsoDate = (str: string) => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;

    const d = new Date(str);

    return d.toISOString() === str; // valid date
};

const applyInlineStyles = (
    draftState: RawDraftContentState,
    inlineStylesMap: { [styledEntityKey: string]: ExtendedRawDraftInlineStyleRange[] },
) => {
    // console.log('map', cloneDeep(inlineStylesMap));

    draftState.blocks.forEach((block) => {
        Object.keys(inlineStylesMap).forEach((styledEntityKey) => {
            const stylesToRewrite = inlineStylesMap[styledEntityKey];
            const styledEntity = find(block.entityRanges, { key: parseInt(styledEntityKey) });

            if (!styledEntity) return;

            block.inlineStyleRanges.forEach((style: ExtendedRawDraftInlineStyleRange) => {
                const styleEnd = style.offset + style.length;

                const entityLengthOffset =
                    isNumber(style.originalEntityLength) &&
                    styledEntity.length - style.originalEntityLength;

                // Rewrite style ranges with the entity length offset
                stylesToRewrite.forEach((styleToRewrite) => {
                    if (JSON.stringify(style) === JSON.stringify(styleToRewrite)) {
                        if (entityLengthOffset && style.originalEntityEnd) {
                            // If mention ends before styling starts - adjust style offset
                            if (style.originalEntityEnd < style.offset) {
                                style.offset += entityLengthOffset;
                                // If mention is contained or ends within styling - adjust style length instead
                            } else if (
                                style.originalEntityEnd >= style.offset &&
                                style.originalEntityEnd <= styleEnd
                            ) {
                                style.length += entityLengthOffset;
                            }

                            return;
                        }
                    }
                });
            });
        });
    });

    return draftState;
};

export const getColor = (color: string, themeColor: string) => {
    return !color || color === 'transparent' ? themeColor : color;
};

export const insertIntoArray = (arr: any[], value: any) => {
    return arr.reduce((result, element, index, array) => {
        result.push(element);

        if (index < array.length - 1) {
            result.push(value);
        }

        return result;
    }, []);
};

// This method receives all page components/blocks, maps them to their DisplayComponent import and
// returns an array of the resulting import promises
export const getBlockImportPromises = (
    components: Component[],
): Promise<typeof import('*.svelte')>[] => {
    const blockImports: Promise<typeof import('*.svelte')>[] = [];
    const componentTypes = components.map((component: Component) => component.componentType);

    // remove duplicates from componentTypes
    const uniqueComponentTypes = [...new Set(componentTypes)];

    debugLog('preloading page blocks:', uniqueComponentTypes);

    // The mapping/switch-case is neccessary since you can't use string variables as import paths
    uniqueComponentTypes.forEach((componentType: string) => {
        switch (componentType) {
            case 'accordion': {
                blockImports.push(
                    import('$lib/components/_DisplayComponents/Accordion/Accordion.svelte'),
                );
                break;
            }
            case 'button': {
                blockImports.push(import('$lib/components/_DisplayComponents/Button.svelte'));
                break;
            }
            case 'divider': {
                blockImports.push(import('$lib/components/_DisplayComponents/Divider.svelte'));
                break;
            }
            case 'embed': {
                blockImports.push(import('$lib/components/_DisplayComponents/Embed/Embed.svelte'));
                break;
            }
            case 'icon': {
                blockImports.push(import('$lib/components/_DisplayComponents/Icon.svelte'));
                break;
            }
            case 'image': {
                blockImports.push(import('$lib/components/_DisplayComponents/Image/Image.svelte'));
                break;
            }
            case 'list': {
                blockImports.push(import('$lib/components/_DisplayComponents/List/List.svelte'));
                break;
            }
            case 'questionForm': {
                blockImports.push(import('$lib/components/_DisplayComponents/CalendlyForm.svelte'));
                blockImports.push(
                    import('$lib/components/_DisplayComponents/FormQuestion/FormQuestion.svelte'),
                );
                break;
            }
            case 'questionMedia': {
                blockImports.push(
                    import('$lib/components/_DisplayComponents/MediaQuestion/MediaQuestion.svelte'),
                );
                break;
            }
            case 'questionMultipleChoice': {
                blockImports.push(
                    import(
                        '$lib/components/_DisplayComponents/MultipleChoiceQuestion/MultipleChoiceQuestion.svelte'
                    ),
                );
                break;
            }
            case 'questionText': {
                blockImports.push(
                    import('$lib/components/_DisplayComponents/TextQuestion/TextQuestion.svelte'),
                );
                break;
            }
            case 'sliderImage': {
                blockImports.push(import('$lib/components/_DisplayComponents/ImageSlider.svelte'));
                break;
            }
            case 'sliderTestimonial': {
                blockImports.push(
                    import('$lib/components/_DisplayComponents/TestimonialSlider.svelte'),
                );
                break;
            }
            case 'text': {
                blockImports.push(import('$lib/components/_DisplayComponents/Text.svelte'));
                break;
            }
            case 'video': {
                blockImports.push(import('$lib/components/_DisplayComponents/Video.svelte'));
                break;
            }
            case 'webinar': {
                blockImports.push(
                    import('$lib/components/_DisplayComponents/Webinar/Webinar.svelte'),
                );
                break;
            }
            default:
                break;
        }
    });

    return blockImports;
};
